"use client";

/**
 * Using IFrames for widgets has an inherent problem:
 *
 * - The size of the iFrame is controlled by the parent page
 * - If the content of the iframe is smaller than the iFrame, the iFrame won't shrink
 * - If the content of the iframe is larger than the iFrame, the iFrame will scroll
 *
 * To work around this, the content of the iFrame can talk to the parent via messages.
 * E.g. when the width of the iFrame changes (resize in the inner window) we can send a
 * message with the height that the iFrame content needs.
 *
 * The parent can then listen to these events and change the height the iFrame accordingly.
 *
 * If there are multiple widgets on the same page, we should make sure to resize the right
 * iFrame.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export function useWidgetResizing(widgetName: string) {
  /**
   * The widgetId id is controlled by the parent
   * Each widget on the parent page should have a unique ID.
   */
  const [widgetId, setWidgetId] = useState<unknown>(null);

  /**
   * This ref should be assigned to the element that should determine the height
   */
  const heightRef = useRef<HTMLElement>(null);

  const announceHeight = useCallback(() => {
    if (!heightRef.current) {
      return;
    }
    sendMessage(`${widgetName}_resize`, {
      widgetId,
      height: heightRef.current.clientHeight,
    });
  }, [widgetName, widgetId]);

  useEffect(() => {
    // Run on announceHeight when the iFrame is resized
    const onResize = () => {
      announceHeight();
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [announceHeight]);

  useEffect(() => {
    // Run on announceHeight on mount
    announceHeight();
  }, [announceHeight]);

  const widgetInit = useCallback(
    (body: Record<string, unknown>) => {
      // The parent can also used the init event to assign a widget id
      setWidgetId(body.widgetId);

      // When the parent sends the init event, trigger announceHeight once
      announceHeight();
    },
    [announceHeight]
  );

  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (
        typeof event.data === "object" &&
        typeof event.data.type === "string" &&
        event.data.type.startsWith(`${widgetName}_`)
      ) {
        switch (event.data.type) {
          case `${widgetName}_init`:
            return widgetInit(event.data.body);
          default:
        }
      }
    },
    [widgetInit, widgetName]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onMessage]);

  return {
    heightRef,
    announceHeight,
  };
}

function sendMessage<T>(type: string, body: T) {
  if (!type.startsWith("fswidget_")) {
    throw new Error("Type must start with fswidget_");
  }

  window.parent.postMessage(
    {
      type,
      body,
    },
    "*"
  );
}
